<template>
  <div class="material-page__wrapper small-scroll-bar small-scroll-bar--black">
    <the-spinner
      v-if="loading"
      value
      fixed
    />

    <school-new-breadcrumbs
      :breadcrumbs="breadcrumbs"
      class="material-page__header-bc"
    />

    <div class="material-page__inner">
      <school-material-viewer
        v-show="!loading"
        :material="material"
        :get-materials-func="$api.getMaterials"
        :materials-banners="banners"
        :user-register-on-webinar="registeredWebinar"
        :have-user="Boolean(user)"
        class="catalog-container"
        @openWebinarRegModal="openWebinarRegModal"
        @signUpToWebinar="manageWebinarUserParticipation"
        @chooseCourse="chooseCourseUpdateLead"
      >
        <template #form>
          <materials-bottom-form
            v-if="material.canSendProposal"
          />
        </template>

        <template #share>
          <material-share
            :material="material"
            vertical
          />
        </template>

        <template #banner>
          <materials-item-proftest />
        </template>

        <template #bottom>
          <material-categories />
          <material-like :material="material" />
          <material-share
            class="material-share-bottom"
            :material="material"
          />
        </template>

        <template
          v-if="isOlympiadCategory"
          #olympiad="item"
        >
          <olympiad-kids-register
            :material="material"
            @close="item.close"
          />
        </template>
      </school-material-viewer>

      <olympiad-other
        v-if="isOlympiadCategory"
        class="catalog-container"
        :material-category="materialCategory"
        :material="material"
      />
      <materials-category-best
        v-else-if="materialCategory"
        class="catalog-container"
        :material-category="materialCategory"
        :material="material"
      />
    </div>

    <school-new-form
      class="catalog-container"
      :land-code="materialCategory ? `new_school_blog_${materialCategory.slug}` : null"
    />

    <school-new-breadcrumbs
      :breadcrumbs="breadcrumbs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import TheSpinner from '@/components/common/theSpinner.vue';
import MaterialsBottomForm from '~/components/materials/MaterialsBottomForm.vue';
import SchoolNewBreadcrumbs from '~/components/v2/school-new/SchoolNewBreadcrumbs.vue';
import MaterialsCategoryBest from '~/components/materials/MaterialsCategoryBest.vue';
import SchoolNewForm from '~/components/v2/school-new/SchoolNewForm.vue';
import MaterialCategories from '~/components/materials/MaterialCategories.vue';
import MaterialLike from '~/components/materials/MaterialLike.vue';
import MaterialShare from '~/components/materials/MaterialShare.vue';
import MaterialsItemProftest from '~/components/materials/MaterialsItemProftest.vue';
import OlympiadKidsRegister from '~/components/materials/olympiad/OlympiadKidsRegister.vue';
import SchoolMaterialViewer from '~/school-preview-components/SchoolMaterialViewer';
import OlympiadOther from '../../components/materials/olympiad/OlympiadOther.vue';

export default {
  name: 'EducationFile',
  components: {
    OlympiadOther,
    OlympiadKidsRegister,
    MaterialsItemProftest,
    MaterialShare,
    MaterialLike,
    MaterialCategories,
    SchoolNewForm,
    MaterialsCategoryBest,
    SchoolNewBreadcrumbs,
    MaterialsBottomForm,
    TheSpinner,
    SchoolMaterialViewer,
  },
  layout: 'DefaultLayoutV2',
  scrollToTop: true,

  data: () => ({
    material: {},
    banners: [],
    loading: false,
    webinarRegBtnTeleportExist: false,
  }),

  async fetch() {
    this.loading = true;
    await Promise.all([this.fetchMaterial(), this.fetchBanners()])
      .finally(() => {
        this.loading = false;
        return this.loading;
      });
  },

  head() {
    return {
      title: this.material?.seoTitle || this.material?.title,
      meta: [
        {
          hid: 'og:title',
          name: 'og:title',
          content: this.material?.seoTitle || this.material?.title,
        },
        {
          hid: 'description',
          name: 'description',
          content: this.material?.description || this.material?.previewText,
        },
        {
          hid: 'og:description',
          name: 'og:description',
          content: this.material?.description || this.material?.previewText,
        },
        {
          hid: 'keywords',
          name: 'keywords',
          content: this.material?.seoKeywords || '',
        },
        {
          hid: 'author',
          name: 'author',
          content: this.material?.authorName || 'Синергия',
        },
      ],
      link: this.headLinks,
    };
  },

  computed: {
    ...mapGetters(['user']),

    publicWebinarId() {
      return this.material.publicWebinar?.id;
    },

    registeredWebinar() {
      if (!this.user) return null;
      if (!this.publicWebinarId) return null;

      return (this.user.publicWebinars || []).find(({ id }) => +id === +this.publicWebinarId);
    },
    headLinks() {
      if (this.$route.name === 'articles-id') {
        return [
          {
            rel: 'canonical',
            // eslint-disable-next-line max-len,vue/max-len
            href: `https://school.synergy.ru/${this.materialCategory.slug}/${this.material.slug || this.material.id}`,
          },
          {
            rel: 'alternate',
            href: `https://school.synergy.ru/articles/${this.material.slug || this.material.id}`,
          },
        ];
      }
      return [
        {
          rel: 'canonical',
          // eslint-disable-next-line max-len,vue/max-len
          href: `https://school.synergy.ru/${this.materialCategory.slug}/${this.material.slug || this.material.id}`,
        },
      ];
    },
    materialCategory() {
      return this.material.categories?.[0] || { slug: 'articles', name: 'Статьи' };
    },
    breadcrumbs() {
      return [
        { text: 'главная', to: '/' },
        { text: 'медиа «за школой»', to: '/articles' },
        ...this.materialCategory ? [{ text: this.materialCategory.name, to: `/${this.materialCategory.slug}` }] : [],
        { text: this.material.title, to: this.$route.path },
      ];
    },

    isOlympiadCategory() {
      return this.$route.params.material_category === 'olympiad';
    },
  },

  watch: {
    'material.id': {
      immediate: true,
      handler(id) {
        if (id) {
          setTimeout(this.addView, 100);
        }
      },
    },
  },

  created() {
    this.scrollToTop();
  },

  // при перезагрузке страницы
  beforeMount() {
    if (this.isOlympiadCategory && !this.material.olympiad) {
      this.fetchOlympiadPack();
      this.fetchOlympiadById();
    }
  },

  methods: {
    addView() {
      this.$api.addViewMaterial(this.material.id);
    },

    fetchMaterial() {
      return this.$api.getMaterial(this.$route.params.id)
        .then((item) => {
          if (item.slug && item.slug !== this.$route.params.id) {
            this.$nuxt.context.redirect(301, { name: this.$route.name, params: { id: item.slug } });
            return item;
          }

          this.material = {
            ...item,
            customAttributes: JSON.parse(item.customAttributes || '{}'),
          };

          // при переходе с другой страницы
          if (this.isOlympiadCategory && process.client) {
            this.fetchOlympiadPack();
            this.fetchOlympiadById();
          }

          return item;
        });
    },

    async fetchBanners() {
      const response = await this.$api.getBanners({ perPage: 99999, published: true });
      this.banners = response.marketingContents.map((banner) => ({
        ...banner,
        data: {
          designVariant: banner.data.designVariant,
          payload: JSON.parse(banner.data.payload),
        },
      })) || [];

      return this.banners;
    },

    async fetchOlympiadPack() {
      if (!this.material.customAttributes?.olympiadPackId) return;
      const olympiadPack = await this.$kidsApi.getOlympiadPackById(
        this.material.customAttributes.olympiadPackId,
      );
      this.$set(this.material, 'olympiadPack', olympiadPack);
    },

    async fetchOlympiadById() {
      if (!this.material.customAttributes?.olympiadId) return;
      const olympiad = await this.$kidsApi.getOlympiadById(
        this.material.customAttributes.olympiadId,
      );
      this.$set(this.material, 'olympiad', olympiad);
    },

    openWebinarRegModal() {
      this.$store.dispatch('modals/openWebinarRegistrationModal', this.material.publicWebinar);
    },

    /**
     *   attended - "Присутствовал"
     *   missed - "Отсутствовал"
     *   viewed_recording - "Посмотрел в записи"
     */
    async manageWebinarUserParticipation(attendanceStatus) {
      if (this.user) {
        await this.$api.manageUserParticipationInPublicWebinar(this.publicWebinarId, attendanceStatus);
        return;
      }

      this.openWebinarRegModal();
    },

    chooseCourseUpdateLead() {
      this.$api.updateLead('Подобрать курс', this.registeredWebinar?.currentMember?.marketingLead?.id);
    },

    scrollToTop() {
      if (process.client) {
        setTimeout(() => {
          window.scrollTo(0, 0);
        });
      } else {
        setTimeout(this.scrollToTop, 50);
      }
    },
  },
};
</script>

<style lang="scss" scoped>

.material-page__wrapper {
  padding-top: calc(var(--scale) * 120);
  padding-bottom: calc(var(--scale) * 58);
  background-color: #ECEDF0;

  @include media-down($size-tablet) {
    padding-top: calc(var(--scale) * 100);
    padding-bottom: calc(var(--scale) * 24);
  }
}

::v-deep(*) {
  font-family: "Onest", sans-serif !important;
}

::v-deep(.main-title) {
  position: relative;

  h2 {
    font-weight: 500;
    font-size: 5.375rem;
    line-height: 110%;
    letter-spacing: -0.04em;
    color: #02120F;

    @include media-down($size-tablet) {
      font-size: 4rem;
    }

    span {
      color: #A7ABAC;
    }
  }
}

.catalog-container {
  padding-inline: calc(var(--scale) * 40);

  @include media-down($size-tablet) {
    padding-inline: calc(var(--scale) * 16);
  }

  @include media-down($size-mobile) {
    padding-inline: calc(var(--scale) * 12);
  }
}

.material-page__header-bc {
  border: none;

  ::v-deep(.school-new-breadcrumbs__item) {
    font-size: calc(var(--scale) * 14);
  }
}

.material-page__inner {
  margin-bottom: calc(var(--scale) * 68);
  width: 100%;
  padding-block: calc(var(--scale) * 40) calc(var(--scale) * 60);
  background-color: #FFFFFF;
  border-radius: calc(var(--scale) * 60);

  @include media-down($size-tablet) {
    margin-bottom: calc(var(--scale) * 40);
    padding-block: calc(var(--scale) * 24);
    border-radius: calc(var(--scale) * 20);
  }

  @include media-down($size-mobile) {
    margin-bottom: calc(var(--scale) * 65);
  }
}

.material-page {
  margin-bottom: calc(var(--scale) * 128);

  @include media-down($size-mobile) {
    margin-bottom: calc(var(--scale) * 80);
  }
}

.material-categories,
.material-like {
  margin-bottom: calc(var(--scale) * 40);

  @include media-down($size-mobile) {
    margin-bottom: calc(var(--scale) * 24);
  }
}

.school-new-form {
  margin-bottom: calc(var(--scale) * 128);

  @include media-down($size-tablet) {
    margin-bottom: calc(var(--scale) * 80);
  }
}

.header-form {
  bottom: 0;
  padding-left: 0;
  padding-right: 0;
}

.webinar-reg-button {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: calc(var(--scale) * 80);

  border: none;
  border-radius: calc(var(--scale) * 24);
  background-color: #FF5319;

  font-size: calc(var(--scale) * 18);
  font-weight: 500;
  line-height: 110%;
  letter-spacing: -0.02em;
  text-align: left;
  text-underline-position: from-font;
  text-decoration-skip-ink: none;
  color: $white-color;

  cursor: pointer;
  transition: background-color .2s ease-out;

  &:hover {
    background-color: #FF9C12;
  }
}
</style>
